import React, { useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import { SVG_COMPONENTS } from '@/assets/admin';
import { COLORS, PADDING } from '@/constants/residents/homeConstants';
import { useAccordionAnimation } from '@/hooks/common/useAccordionAnimation';

interface ExpandableInfoItemProps {
  title: string;
  detail: string;
  isExpanded: boolean;
  onPress: () => void;
}

export const ExpandableInfoItem: React.FC<ExpandableInfoItemProps> = ({ 
  title, 
  detail, 
  isExpanded, 
  onPress 
}) => {
  const { setOpen, animatedHeightStyle, animatedChevronStyle, onContentLayout } = useAccordionAnimation({ initiallyOpen: isExpanded });

  useEffect(() => {
    setOpen(isExpanded);
  }, [isExpanded, setOpen]);

  return (
    <View style={styles.container}>
      <TouchableOpacity 
        style={styles.card}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={`${title}. ${isExpanded ? 'Expanded' : 'Collapsed'}`}
        accessibilityHint="Double tap to expand or collapse"
        activeOpacity={1}
      >
        <View style={styles.header}>
          <Text style={styles.title}>{title}</Text>
          
          <Animated.View style={animatedChevronStyle}>
            <SVG_COMPONENTS.CHEVRON_DOWN
              width={20}
              height={20}
              color={COLORS.textSubtitle}
            />
          </Animated.View>
        </View>
        
        <Animated.View style={animatedHeightStyle}>
          <View onLayout={onContentLayout} style={styles.detailInner}>
            <View style={styles.detailContainer}>
              <Text style={styles.detail}>{detail}</Text>
            </View>
          </View>
        </Animated.View>
      </TouchableOpacity>
      
      <View style={styles.divider} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 0,
  },
  divider: {
    height: 1,
    backgroundColor: COLORS.separator,
    marginHorizontal: PADDING.padding4,
    marginTop: PADDING.padding1,
  },
  card: {
    backgroundColor: COLORS.background,
    padding: PADDING.padding4,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: COLORS.textPrimary,
    marginRight: PADDING.padding2,
  },
  detailInner: {
    position: 'absolute',
    width: '100%',
  },
  detailContainer: {
    marginTop: PADDING.padding1,
    paddingTop: PADDING.padding1,
  },
  detail: {
    fontSize: 14,
    fontWeight: '400',
    color: COLORS.textPrimary,
    lineHeight: 20,
  },
});
